"use client";

import { Video } from "@/models/video";
import {
  User,
  UserPass,
  userPassStatus,
  Customer,
  UserRoles,
  ExtendedFormResponseInput,
} from "@/models/user";
import {
  DocumentSnapshot,
  collection,
  QueryDocumentSnapshot,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  orderBy,
  setDoc,
  deleteDoc,
  updateDoc,
  runTransaction,
  where,
  addDoc,
  collectionGroup,
  serverTimestamp,
  startAfter,
  DocumentData,
  getCountFromServer,
  Timestamp,
} from "firebase/firestore";
import { PartialDeep } from "type-fest";
import { auth, db, storage } from "@/helpers/firebase/firebase";
import { getCycleDocumentId, getFormCycle, isCycleOpen } from "@/app/form/[...slug]/form-cycles";
import { Collections } from "@/helpers/utils/collections";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { FormResponseStatus } from "@/models/form-types";
import { formNames } from "@/app/form/[...slug]/schemas";
import {
  getFestivalYearFromFormDocumentId,
  getSlugFromFormId,
} from "../utils/utility-functions";
import { use } from "react";
import { isVillageIntake, villageInterestSchema, villageVendorSchema } from "@/helpers/utils/village-schema";
import { VillageRecruitmentStage } from "@/helpers/utils/village-intake";

const userMap = (doc: DocumentSnapshot): User => {
  const data = doc.data() as User;
  data.uid = doc.id;
  data.motivations = data.motivations ?? doc.data()?.["motivation"] ?? [];
  data.publicProfileInfo = data.publicProfileInfo ?? {
    about: "",
    linkedInUrl: "",
    twitterUrl: "",
    instagramUrl: "",
    facebookUrl: "",
    tiktokUrl: "",
    spotifyUrl: "",
    twitchUrl: "",
    profileTheme: "#FFEFD0",
  };
  return data;
};

const userPassesMap = (doc: QueryDocumentSnapshot): UserPass => {
  const data = doc.data();
  data.status = data.status ?? userPassStatus.UNASSIGNED;
  const statusExists = Object.values(userPassStatus).some(
    (x) => x.toLowerCase() == data.status.toLowerCase().toString()
  );
  return {
    ...data,
    id: doc.id,
    status: statusExists ? data.status : userPassStatus.UNASSIGNED,
    timestamp: mapTimestampToDate(data.timestamp),
    assignedTo: !!data.assignedTo
      ? {
          ...data.assignedTo,
          passAssignRequestTimestamp: mapTimestampToDate(
            data.assignedTo.passAssignRequestTimestamp
          ),
          passAssignConfirmTimestamp: mapTimestampToDate(
            data.assignedTo.passAssignConfirmTimestamp
          ),
        }
      : undefined,
  } as UserPass;
};

const videosMap = (doc: DocumentSnapshot): Video =>
  ({
    id: doc.id,
    ...doc.data(),
  } as Video);

const mapTimestampToDate = (timestamp: any): Date | undefined => {
  if (!timestamp) return undefined;
  // Firestore Timestamp object (has toDate method)
  if (typeof timestamp.toDate === "function") return timestamp.toDate();
  // Firestore serialized format { seconds, nanoseconds }
  if (timestamp.seconds !== undefined) return new Date(timestamp.seconds * 1000);
  if (timestamp._seconds !== undefined) return new Date(timestamp._seconds * 1000);
  // Already a Date
  if (timestamp instanceof Date) return timestamp;
  // String or number
  if (typeof timestamp === "string" || typeof timestamp === "number") {
    const parsed = new Date(timestamp);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }
  return undefined;
};

export async function getUserwithEmail(email: string): Promise<User | null> {
  const usersRef = collection(db, Collections.USERS);
  const q = query(usersRef, where("email", "==", email), limit(1));
  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) return null;
  return userMap(querySnapshot.docs[0]);
}

export async function getUserwithPhoneNumber(
  number: string
): Promise<User | null> {
  const usersRef = collection(db, Collections.USERS);
  const q = query(usersRef, where("phoneNumber", "==", number), limit(1));
  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) return null;
  return userMap(querySnapshot.docs[0]);
}

export async function createUser(user: User, uid: string): Promise<boolean> {
  try {
    await setDoc(doc(db, Collections.USERS, uid), user);
    return true;
  } catch (e) {
    return false;
  }
}

export async function updateUser(user: PartialDeep<User>): Promise<boolean> {
  try {
    const { passes, ...rest } = user;
    const userDocRef = doc(db, `${Collections.USERS}/${user.uid}`);
    await updateDoc(userDocRef, { ...rest });
    return true;
  } catch (e) {
    return false;
  }
}

export async function getUserInfoById(
  userId: string
): Promise<User | undefined> {
  return await getUserInfoByDocumentId(userId);
}

async function getUserInfoByDocumentId(id: string): Promise<User | undefined> {
  const userDocRef = doc(db, `${Collections.USERS}/${id}`);
  const userDoc = await getDoc(userDocRef);

  if (!userDoc.exists()) return undefined;
  return userMap(userDoc);
}

export async function getUserPasses(uid: string): Promise<UserPass[]> {
  const passSnapshot = await getDocs(
    collection(db, `${Collections.USERS}/${uid}/${Collections.PASSES}`)
  );
  const passData = passSnapshot.docs.map(userPassesMap);
  // Defensive filter: hide orphan passes created by the cloud function when a
  // non-CLabs Stripe webhook was processed (e.g. Buy Better Funnel leakage).
  // A valid CLabs pass always has either a passId or a ticketTypeSlug.
  return passData.filter((pass) => {
    const hasPassId = !!pass.passId && pass.passId.length > 0;
    const hasTicketType =
      !!pass.ticketTypeSlug && pass.ticketTypeSlug.length > 0;
    return hasPassId || hasTicketType;
  });
}

export async function getUserVideoInfoByDocumentId(
  userId: string
): Promise<Video[]> {
  try {
    const userUploadedVideosCollection = collection(
      db,
      `${Collections.USERS}/${userId}/${Collections.VIDEO_RESPONSE}`
    );
    let videoQuery = query(
      userUploadedVideosCollection,
      orderBy("timestamp", "desc")
    );
    const videoSnapshot = await getDocs(videoQuery);
    const videoData = videoSnapshot.docs.map(videosMap);
    return videoData;
  } catch (e) {
    // console.log("Error while adding form response", e);
    return [];
  }
}

export async function addPublicProfileResponse(
  userId: string,
  publicProfileResponse: any
): Promise<boolean> {
  try {
    await addDoc(
      collection(
        db,
        `${Collections.USERS}/${userId}/${Collections.PUBLIC_PROFILE_RESPONSE}`
      ),
      publicProfileResponse
    );
    return true;
  } catch (e) {
    // console.log("Error while adding form response", e);
    return false;
  }
}

export async function getFormResponseEntry(
  userId: string,
  formId: string
): Promise<any> {
  // set a cutoff of current year
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - 1);

  const formResponseDoc = await getDoc(
    doc(
      db,
      `${Collections.USERS}/${userId}/${Collections.FORM_RESPONSES}`,
      formId
    )
  );
  return formResponseDoc.exists() ? formResponseDoc.data() : undefined;
}

const PREFILL_FIELDS = [
  "firstName",
  "lastName",
  "email",
  "phone",
  "companyName",
  "jobTitle",
  "portfolioWebsite",
  "portfolioLink",
  "socialMediaHandle",
  "primaryContent",
  "basedIn",
] as const;

export async function getPrefillFromPriorSubmissions(
  userId: string,
  targetFieldKeys: string[]
): Promise<{ data: Record<string, string>; count: number }> {
  const responsesRef = collection(
    db,
    `${Collections.USERS}/${userId}/${Collections.FORM_RESPONSES}`
  );
  const q = query(
    responsesRef,
    where("status", "==", FormResponseStatus.IN_REVIEW),
    orderBy("timestamp", "desc"),
    limit(10)
  );
  const snapshot = await getDocs(q);

  const prefillData: Record<string, string> = {};

  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    for (const key of targetFieldKeys) {
      if (
        prefillData[key] === undefined &&
        data[key] &&
        typeof data[key] === "string" &&
        data[key].trim() !== "" &&
        PREFILL_FIELDS.includes(key as (typeof PREFILL_FIELDS)[number])
      ) {
        prefillData[key] = data[key];
      }
    }
  }

  return { data: prefillData, count: Object.keys(prefillData).length };
}

// Firestore rejects any document containing `undefined`. A numeric year is a
// festival cycle, an explicit null is a standalone intake, and an absent value
// (legacy callers) omits the field entirely.
function festivalYearField(
  festivalYear: number | null | undefined
): { festivalYear?: number | null } {
  if (typeof festivalYear === "number") return { festivalYear };
  if (festivalYear === null) return { festivalYear: null };
  return {};
}

function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined)
  ) as T;
}

export type FormResponseWriteResult =
  | { ok: true; recruitmentStage?: VillageRecruitmentStage }
  | { ok: false; reason: "already_submitted" | "write_failed" | "closed" | "invalid_identity" | "no_session" | "invalid_status" | "invalid_answers" | "rate_limited" };

async function writeVillageIntake(userData: User, body: Record<string, unknown>) {
  if (!userData.uid || auth.currentUser?.uid !== userData.uid) throw new Error("no_session");
  const token = await auth.currentUser.getIdToken();
  const response = await fetch("/api/village-intake", {
    method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  return response.json();
}

function validateApplicantWrite(
  userData: User,
  documentId: string,
  identity: { formId: string; festivalYear: number | null }
) {
  if (!userData.uid || auth.currentUser?.uid !== userData.uid) return "no_session" as const;
  const cycle = getFormCycle(identity.formId.replaceAll("_", "/"));
  if (!cycle || getCycleDocumentId(cycle) !== documentId || cycle.festivalYear !== identity.festivalYear) {
    return "invalid_identity" as const;
  }
  if (!isCycleOpen(cycle)) return "closed" as const;
  return null;
}

function identityMatches(data: DocumentData, uid: string, formId: string, festivalYear: number | null): boolean {
  const cycle = getFormCycle(formId.replaceAll("_", "/"))!;
  return Object.entries({ uid, formId, festivalYear, eventKey: cycle.eventKey, cycleKey: cycle.cycleKey })
    .every(([key, value]) => data[key] === undefined || data[key] === value);
}

export type DraftCreateResult =
  | { created: true; status: FormResponseStatus.NOT_SUBMITTED }
  | { created: false; status: string; data: DocumentData };

function formResponseRef(uid: string, documentId: string) {
  return doc(
    db,
    `${Collections.USERS}/${uid}/${Collections.FORM_RESPONSES}`,
    documentId
  );
}

function applicantFields(userData: User) {
  return {
    uid: userData.uid,
    userFirstName: userData.firstName ?? "",
    userLastName: userData.lastName ?? "",
    userEmail: userData.email ?? "",
    userPhone: userData.phoneNumber ?? "",
  };
}

// Atomic create-if-absent. Two tabs that both believe there is no draft cannot
// both write: the second transaction sees the first tab's document and returns
// it untouched, so a submitted record is never reset to a draft.
export async function createFormResponseDraft(
  userData: User,
  documentId: string,
  formId: string,
  festivalYear: number | null
): Promise<DraftCreateResult> {
  if (isVillageIntake(formId)) {
    const reason = validateApplicantWrite(userData, documentId, { formId, festivalYear });
    if (reason) throw new Error(reason);
    const result = await writeVillageIntake(userData, { formId, action: "create" });
    if (result.ok === false) throw new Error(result.reason);
    return result;
  }
  const ref = formResponseRef(userData.uid, documentId);
  return runTransaction(db, async (tx): Promise<DraftCreateResult> => {
    const snap = await tx.get(ref);
    const reason = validateApplicantWrite(userData, documentId, { formId, festivalYear });
    if (reason) throw new Error(reason);
    if (snap.exists()) {
      const data = snap.data() as DocumentData;
      if (!identityMatches(data, userData.uid, formId, festivalYear)) throw new Error("invalid_identity");
      return { created: false, status: String(data.status ?? ""), data };
    }
    const cycle = getFormCycle(formId.replaceAll("_", "/"))!;
    tx.set(ref, {
      formId,
      eventKey: cycle.eventKey,
      cycleKey: cycle.cycleKey,
      ...festivalYearField(festivalYear),
      ...applicantFields(userData),
      status: FormResponseStatus.NOT_SUBMITTED,
      timestamp: serverTimestamp(),
    });
    return { created: true, status: FormResponseStatus.NOT_SUBMITTED };
  });
}

// Applicant step-save or final submit. `identity.formId` is the canonical,
// unsuffixed form key and this is the only write path allowed to set it. The
// transaction refuses to write over a record that is no longer a draft, so a
// stale tab cannot downgrade IN_REVIEW (or a decided status) back to
// NOT_SUBMITTED, and cannot re-submit over a decided application.
export async function saveFormResponse(
  userData: User,
  documentId: string,
  formResponse: Record<string, unknown>,
  identity: { formId: string; festivalYear: number | null }
): Promise<FormResponseWriteResult> {
  if (isVillageIntake(identity.formId)) {
    const reason = validateApplicantWrite(userData, documentId, identity);
    if (reason) return { ok: false, reason };
    if (![FormResponseStatus.NOT_SUBMITTED, FormResponseStatus.IN_REVIEW].includes(formResponse.status as FormResponseStatus)) return { ok: false, reason: "invalid_status" };
    const shape = identity.formId === "vendor" ? villageVendorSchema : villageInterestSchema;
    const answers = Object.fromEntries(Object.entries(formResponse).filter(([key, value]) => Object.hasOwn(shape, key) && value !== null && value !== undefined));
    try {
      return await writeVillageIntake(userData, {
        formId: identity.formId, action: "save", answers,
        submit: formResponse.status === FormResponseStatus.IN_REVIEW,
        ...(formResponse.intakeSource === "village-expo" ? { source: "village-expo" } : {}),
        ...(formResponse.attribution ? { attribution: formResponse.attribution } : {}),
      });
    } catch { return { ok: false, reason: "write_failed" }; }
  }
  const ref = formResponseRef(userData.uid, documentId);
  try {
    return await runTransaction(
      db,
      async (tx): Promise<FormResponseWriteResult> => {
        const snap = await tx.get(ref);
        const reason = validateApplicantWrite(userData, documentId, identity);
        if (reason) return { ok: false, reason };
        if (![FormResponseStatus.NOT_SUBMITTED, FormResponseStatus.IN_REVIEW].includes(formResponse.status as FormResponseStatus)) {
          return { ok: false, reason: "invalid_status" };
        }
        const data = snap.exists() ? snap.data() : undefined;
        if (data && !identityMatches(data, userData.uid, identity.formId, identity.festivalYear)) {
          return { ok: false, reason: "invalid_identity" };
        }
        if (
          data && data.status !== FormResponseStatus.NOT_SUBMITTED
        ) {
          return { ok: false, reason: "already_submitted" };
        }
        const cycle = getFormCycle(identity.formId.replaceAll("_", "/"))!;
        tx.set(
          ref,
          {
            ...stripUndefined(formResponse),
            formId: identity.formId,
            eventKey: cycle.eventKey,
            cycleKey: cycle.cycleKey,
            ...festivalYearField(identity.festivalYear),
            ...applicantFields(userData),
            timestamp: serverTimestamp(),
          },
          { merge: true }
        );
        return { ok: true };
      }
    );
  } catch (e) {
    console.error("[form_responses] write failed", {
      formId: identity.formId,
      code: (e as { code?: string })?.code ?? "unknown",
    });
    return { ok: false, reason: "write_failed" };
  }
}

const FORM_RESPONSE_IDENTITY_FIELDS = new Set(["formId", "festivalYear", "uid", "eventKey", "cycleKey", "timestamp"]);

// Reviewer/admin merge (notes, attachments, decisions). Never writes identity
// metadata, so the admin list query on the canonical `formId` keeps matching
// after a note is saved on a year-suffixed document.
export async function updateFormResponseFields(
  uid: string,
  documentId: string,
  fields: Record<string, unknown>
): Promise<boolean> {
  const safe = Object.fromEntries(
    Object.entries(fields).filter(
      ([key, value]) =>
        !FORM_RESPONSE_IDENTITY_FIELDS.has(key) && value !== undefined
    )
  );
  try {
    await updateDoc(formResponseRef(uid, documentId), safe);
    return true;
  } catch (e) {
    console.error("[form_responses] admin update failed", {
      documentId,
      code: (e as { code?: string })?.code ?? "unknown",
    });
    return false;
  }
}

export async function addExtendedInputsToFormResponse(
  inputResponse: ExtendedFormResponseInput
): Promise<boolean> {
  try {
    const { id, ...rest } = inputResponse;
    await addDoc(
      collection(
        db,
        `${Collections.USERS}/${inputResponse.userId}/${Collections.FORM_RESPONSES}/${inputResponse.formId}/${Collections.EXTENDED_INPUTS}`
      ),
      { ...rest, timestamp: serverTimestamp() }
    );
    return true;
  } catch (e) {
    return false;
  }
}

export async function addVideoResponseToDb(
  userId: string,
  videoResponse: any
): Promise<boolean> {
  try {
    await addDoc(
      collection(
        db,
        `${Collections.USERS}/${userId}/${Collections.VIDEO_RESPONSE}`
      ),
      videoResponse
    );
    return true;
  } catch (e) {
    return false;
  }
}

export async function getTotalVideoCount(): Promise<number> {
  const uploadedVideosCollectionGroupRef = collectionGroup(
    db,
    "uploaded_videos"
  );
  const snapshot = await getCountFromServer(uploadedVideosCollectionGroupRef);
  return snapshot.data().count;
}

export async function getVideosForAdmin(
  rowsPerPage: number,
  lastDoc: QueryDocumentSnapshot<DocumentData> | null = null
): Promise<{
  videos: Video[];
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
}> {
  const uploadedVideosCollectionGroupRef = collectionGroup(
    db,
    "uploaded_videos"
  );

  let videoQuery;
  if (lastDoc) {
    videoQuery = query(
      uploadedVideosCollectionGroupRef,
      orderBy("timestamp", "desc"),
      startAfter(lastDoc),
      limit(rowsPerPage)
    );
  } else {
    videoQuery = query(
      uploadedVideosCollectionGroupRef,
      orderBy("timestamp", "desc"),
      limit(rowsPerPage)
    );
  }

  const querySnapshot = await getDocs(videoQuery);
  const uploadedVideos = querySnapshot.docs.map(videosMap);

  const lastVisible =
    querySnapshot.docs.length > 0
      ? querySnapshot.docs[querySnapshot.docs.length - 1]
      : null;

  return { videos: uploadedVideos, lastVisible };
}

export async function updateVideo(video: Video) {
  try {
    const queryData = query(
      collectionGroup(db, "uploaded_videos"),
      where("url", "==", video.url)
    );
    const querySnapshot = await getDocs(queryData);
    if (!querySnapshot.empty) {
      const docRef = doc(db, querySnapshot.docs[0].ref.path);
      await updateDoc(docRef, { ...video, lastModified: serverTimestamp() });
      const updatedVideo = await getDoc(docRef);
      return videosMap(updatedVideo);
    } else {
      return null;
    }
  } catch (e) {
    // console.error(e);
    return null;
  }
}

export async function deleteVideo(video: Video) {
  try {
    const queryData = query(
      collectionGroup(db, "uploaded_videos"),
      where("url", "==", video.url)
    );
    const querySnapshot = await getDocs(queryData);
    if (!querySnapshot.empty) {
      const docRef = querySnapshot.docs[0].ref;
      await deleteDoc(docRef);
    }
  } catch (e) {
    // console.error(e);
    return null;
  }
}

// get all form reponses for current year
export async function getFormResponses(
  userId: string
): Promise<Record<string, any>[]> {
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - 1);
  const formResponses = await getDocs(
    query(
      collection(
        db,
        `${Collections.USERS}/${userId}/${Collections.FORM_RESPONSES}`
      ),
      where("timestamp", ">", cutoff),
      orderBy("timestamp", "desc")
    )
  );

  const formResponsesData = formResponses.docs.map((doc) => {
    const slug = getSlugFromFormId(doc.id);
    const festivalYear =
      doc.data().festivalYear ?? getFestivalYearFromFormDocumentId(doc.id);
    const baseName = formNames[slug];

    return {
      ...doc.data(),
      name:
        baseName && festivalYear ? `${baseName} (${festivalYear})` : baseName,
      festivalYear,
      id: doc.id,
    };
  });

  return formResponsesData;
}

export async function removeFormResponseFromDb(
  userId: string,
  formResponseId: string
) {
  await deleteDoc(
    doc(
      db,
      `${Collections.USERS}/${userId}/${Collections.FORM_RESPONSES}`,
      formResponseId
    )
  );
}

export async function getAllPartners() {
  try {
    const usersRef = collection(db, Collections.USERS);
    const q = query(usersRef, where("role", "==", "partner"));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) return [];
    return querySnapshot.docs.map(userMap);
  } catch (e) {
    return [];
  }
}

export async function getAllUsersAndPartners() {
  try {
    const usersRef = collection(db, Collections.USERS);
    const q = query(usersRef, where("role", "in", ["user", "partner"]));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) return [];
    return querySnapshot.docs.map(userMap);
  } catch (e) {
    return [];
  }
}

export async function getEventPartners(eventId: string) {
  const collectionRef = collection(db, Collections.USERS);

  const q = query(
    collectionRef,
    where("role", "==", UserRoles.partner),
    where("partnerEvents", "array-contains", eventId)
  );
  const eventSnapshot = await getDocs(q);
  if (eventSnapshot.empty) {
    return [];
  }
  const partners = eventSnapshot.docs.map((doc) => userMap(doc));
  return partners || [];
}

export async function getPartnersWithSharedForms() {
  const collectionRef = collection(db, Collections.USERS);

  const q = query(collectionRef, where("role", "==", UserRoles.partner));
  const eventSnapshot = await getDocs(q);
  if (eventSnapshot.empty) {
    return [];
  }
  const partners = eventSnapshot.docs
    .map((doc) => userMap(doc))
    .filter((partner) => (partner.sharedForms?.length ?? 0) > 0);
  return partners || [];
}
